import { Injectable } from '@angular/core';
import * as moment from 'moment';
import {
  Building,
  BuildingOpeningDay,
  BuildingOpeningHour,
  Invoice,
  InvoiceLine,
  InvoiceOrganization,
  InvoicePlace,
  Member,
  Organization,
  Room,
  RoomBooking,
  RoomBookingServiceBooking,
  Service,
  ServiceBooking,
} from '../../models';
import { ServiceBookingInvoiceService, ServiceInvoiceData } from './service-booking-invoice.service';
import { BuildingService } from '../../data/booking/building.service';
import { RoomService } from '../../data/booking/room.service';
import { ServiceService } from '../../data/booking/service.service';
import { MemberService } from '../../data/member/member.service';
import { RoomBookingService } from '../../data/booking/room-booking.service';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { InvoicingService } from './invoicing.service';
import { BuildingOpeningHoursService } from '../../data';
import { OpeningDaysAndHoursService } from '../opening-days-and-hours.service';

export interface HalfDayLimits {
  am: {
    maxTime?: string;
    minTime?: string;
  };
  pm: {
    maxTime?: string;
    minTime?: string;
  };
}

export class RoomBookingInvoiceData {
  nbrFullDay: number;
  nbrHalfDay: number;
  roomTotalPrice: number;
  services: ServiceInvoiceData[];
  servicesTotal: number;

  constructor() {
    this.nbrHalfDay = 0;
    this.nbrFullDay = 0;
    this.roomTotalPrice = 0;
  }
}

/**
 * Service containing tools for converting the data of a RoomBooking into information relevant to invoicing
 */
@Injectable()
export class RoomBookingInvoiceService extends InvoicingService {

  buildings: Building[] = [];
  rooms: Room[] = [];
  services: Service[] = [];

  private halfDayLimits: HalfDayLimits;

  private roomBookingInvoice: Invoice = new Invoice();

  constructor(
    private buildingService: BuildingService,
    private buildingOpeningHoursService: BuildingOpeningHoursService,
    private openingDaysAndHoursService: OpeningDaysAndHoursService,
    private serviceService: ServiceService,
    private roomService: RoomService,
    private roomBookingService: RoomBookingService,
    private memberService: MemberService,
    private datePipe: DatePipe,
    private serviceBookingService: ServiceBookingInvoiceService,
  ) {
    super();

    this.buildingService.getAll().subscribe(buildings => {
      this.buildings = buildings;
    });
    this.serviceService.getAll().subscribe(services => {
      this.services = services['hydra:member'];
    });
    this.roomService.getAll().subscribe(rooms => {
      this.rooms = rooms['hydra:member'];
    });
  }

  private static getServicesTotal(invoiceData: ServiceInvoiceData[]) {
    let total = 0;

    for (const serviceData of invoiceData) {
      total += serviceData.quantity * serviceData.cost;
    }

    return total;
  }

  getInvoice(roomBooking: RoomBooking): Observable<Invoice> {
    return combineLatest([
      this.setInvoiceSupplier(roomBooking),
      this.setInvoiceCustomer(roomBooking),
      this.setInvoiceLines(roomBooking),
    ]).pipe(map(([supplier, customer, lines]) => {
      this.roomBookingInvoice.header = super.getInvoiceHeader();
      this.roomBookingInvoice.supplier = supplier.supplier;
      this.roomBookingInvoice.supplierEco = supplier.supplierEco;
      this.roomBookingInvoice.customer = customer.customer;
      this.roomBookingInvoice.customerEco = customer.customerEco;
      this.roomBookingInvoice.lines = lines;
      return this.roomBookingInvoice;
    }));
  }

  /**
   * Return the Room Booking invoice data of a room booking including
   * the number of half and full day booked and the total price.
   * RoomBookingInvoiceData is an easy format to display.
   *
   * @param startDate The start of the booking
   * @param endDate The end the booking
   * @param room The room booked
   * @param roomBooking The room booking linked to this invoice
   *
   * @return RoomBookingInvoiceData;
   */
  getRoomBookingInvoiceData(
    startDate: Date,
    endDate: Date,
    room: Room,
    roomBooking: RoomBooking): RoomBookingInvoiceData {
    const invoiceData = new RoomBookingInvoiceData();
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    const startTimeMoment = moment(0).set({hour: startDate.getHours(), minute: startDate.getMinutes()});
    const endTimeMoment = moment(0).set({hour: endDate.getHours(), minute: endDate.getMinutes()});

    const amEnd = moment(this.halfDayLimits.am.maxTime);
    const pmStart = moment(this.halfDayLimits.pm.minTime);

    if (startMoment.day() === endMoment.day()) {
      if (this.isFullDay(startDate, endDate, this.halfDayLimits)) {
        invoiceData.nbrFullDay = 1;
        invoiceData.roomTotalPrice = room.defaultFullDayPrice;
      } else {
        invoiceData.nbrHalfDay = 1;
        invoiceData.roomTotalPrice = room.defaultHalfDayPrice;
      }
    } else {
      let totalPrice = 0;
      let numberOfDays = endMoment.diff(startMoment, 'days') + 1; // +1 to include the start

      if (startTimeMoment.isSameOrAfter(pmStart)) {
        numberOfDays--;
        invoiceData.nbrHalfDay++;
        totalPrice += room.defaultHalfDayPrice;
      }
      if (endTimeMoment.isSameOrBefore(amEnd)) {
        numberOfDays--;
        invoiceData.nbrHalfDay++;
        totalPrice += room.defaultHalfDayPrice;
      }
      // If the booking start after noon and end at morning, we must restore a day
      if (startTimeMoment.isSameOrAfter(pmStart) && endTimeMoment.isSameOrBefore(amEnd)) {
        numberOfDays++;
      }
      totalPrice += room.defaultFullDayPrice * numberOfDays;
      invoiceData.nbrFullDay = numberOfDays;
      invoiceData.roomTotalPrice = totalPrice;
    }

    invoiceData.services = this.serviceBookingService
      .getServiceInvoiceData(roomBooking.serviceBookings as ServiceBooking[]);

    invoiceData.servicesTotal = RoomBookingInvoiceService.getServicesTotal(invoiceData.services);

    return invoiceData;
  }

  setHalfDayLimits(halfDayLimits: HalfDayLimits) {
    this.halfDayLimits = halfDayLimits;
  }

  /**
   * Set and return both customer and customerEco according to the given room booking.
   * Set also the public roomBookingInvoice headers with customer's informations.
   *
   * @param roomBooking
   * @return
   */
  setInvoiceCustomer(roomBooking): Observable<{ customer: InvoiceOrganization; customerEco: InvoicePlace }> {
    return this.memberService.get(roomBooking.owner.id).pipe(
      switchMap(owner => {
        owner = new Member(owner);
        this.roomBookingInvoice.header.customerEmail = owner.getEmail();
        this.roomBookingInvoice.header.customerContact = owner.getFullName();
        return super.getInvoiceableNumber(owner);
      }),
      map(invoiceableNumber => ({
          customer: invoiceableNumber instanceof InvoiceOrganization ? invoiceableNumber : null,
          customerEco: invoiceableNumber instanceof InvoicePlace ? invoiceableNumber : null,
        })));
  }

  /**
   * Returns the room booking invoicing lines for the room reservation and
   * associated services if they exist, AS EXPECTED BY MYINVOICE
   *
   * @return Observable<InvoiceLine[]>
   * @param roomBooking
   */
  // TODO REFACTOR
  //  Draw RoomBooking invoice lines (including service booking lines without using "this.getRoomBookingInvoiceData()")
  //  To optimize treatment and improve code readeability
  setInvoiceLines(roomBooking: RoomBooking) {
    Object.assign(roomBooking.room, this.rooms.find(
      room => room['@id'] === roomBooking.room['@id'],
    ));

    roomBooking.room.building = this.buildings.find(
      building => building['@id'] === roomBooking.room.building,
    );

    return combineLatest([
      this.buildingService.getBuildingOpeningDays(roomBooking.room.building.id),
      this.buildingOpeningHoursService.getAll({'openingDay.building.id': roomBooking.room.building.id}),
    ]).pipe(
      switchMap(([
                   buildingOpeningDays,
                   buildingOpeningHours,
                 ]) => {
        roomBooking.room.building.openingDays = this.openingDaysAndHoursService.mergeOpeningDaysAndHours(
          buildingOpeningDays['hydra:member'],
          buildingOpeningHours['hydra:member'],
        );
        return this.roomBookingService.getServiceBookings(roomBooking.id);
      }),
      map(serviceBookings => {
        roomBooking.serviceBookings = serviceBookings['hydra:member'] as RoomBookingServiceBooking[];

        roomBooking.serviceBookings = this.serviceBookingService
          .enhanceServiceBookingsService(roomBooking.serviceBookings);

        this.setHalfDayLimitsForInvoicing(roomBooking.room.building);

        const invoiceLines = this.getRoomBookingInvoiceData(
          new Date(roomBooking.startDate),
          new Date(roomBooking.endDate),
          roomBooking.room,
          roomBooking,
        );

        const roomBookingLine: InvoiceLine = new InvoiceLine();
        const formatStartDate = this.datePipe.transform(roomBooking.startDate, 'short');
        const formatEndDate = this.datePipe.transform(roomBooking.endDate, 'short');
        const currency = (roomBooking.room.building as Building).currency;

        roomBookingLine.itemDescription = `${roomBooking.name} | ${formatStartDate} - ${formatEndDate}`;
        roomBookingLine.billingObject = 'BOOKING';
        roomBookingLine.price.invoiceAmount.currency = currency;
        roomBookingLine.price.invoiceAmount.unitAmount = invoiceLines.roomTotalPrice;
        roomBookingLine.price.quantity = 1;
        roomBookingLine.price.vatCode = 1;

        const servicesLines: InvoiceLine[] = this.serviceBookingService.getServiceBookingsLines(
          invoiceLines.services,
          currency,
        );

        return [roomBookingLine, ...servicesLines];
      }),
    );
  }

  setInvoiceSupplier(roomBooking): Observable<{ supplier?: InvoiceOrganization; supplierEco?: InvoicePlace }> {
    const buildingId = roomBooking.room.building.split('/').pop();
    return this.buildingService.get(buildingId).pipe(
      map(building => {
        const invoiceableNumber = (building.manager as Organization).costCenter;
        return {
          supplier: null,
          supplierEco: new InvoicePlace({costCenter: invoiceableNumber}),
        };
      }),
    );
  }

  private isFullDay(startDate: Date, endDate: Date, openingHours: HalfDayLimits): boolean {
    const amStart = openingHours.am.maxTime.split('T')[1].substring(0, 5);
    const pmEnd = openingHours.pm.minTime.split('T')[1].substring(0, 5);

    const isStartDuringAm = moment(startDate).format('HH:mm') < amStart;
    const isEndDuringPm = moment(endDate).format('HH:mm') > pmEnd;

    return isStartDuringAm && isEndDuringPm;
  }

  /**
   * Set the building day limits to properly call this.getRoomBookingInvoiceData() next.
   * These treatment were removed from this.setInvoiceLines() and will be deleted after refactor
   *
   * @param building
   */
  // TODO Must be deleted
  private setHalfDayLimitsForInvoicing(building: Building): void {
    const firstOpenDay = building.openingDays[0] as BuildingOpeningDay;
    const buildingOpeningMorning: BuildingOpeningHour = firstOpenDay.openingHours[0] as BuildingOpeningHour;
    const buildingOpeningAfternoon: BuildingOpeningHour = firstOpenDay.openingHours[1] as BuildingOpeningHour;

    this.setHalfDayLimits({
      am: {
        minTime: moment(buildingOpeningMorning.periodStart, 'HH:mm').format('HH:mm'),
        maxTime: moment(buildingOpeningMorning.periodEnd, 'HH:mm').format('HH:mm'),
      },
      pm: {
        minTime: moment(buildingOpeningAfternoon.periodStart, 'HH:mm').format('HH:mm'),
        maxTime: moment(buildingOpeningAfternoon.periodEnd, 'HH:mm').format('HH:mm'),
      },
    });
  }
}
